import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, MouseEvent, useCallback } from 'react';

import Header from '../../components/header/header.component';
import ModuleSummary from '../../components/module-summary/module-summary.component';
import { couponSdk } from '../../services/coupon.service';
import ResponseCodes from '../../constants/response.codes';
import { Coupon } from '../../types/coupon.type';
import { TableRow } from '../../types/table-row.type';
import Table from '../../components/table/table.component';
import FilteringButton from '../../components/filtering-button/filtering-button.component';
import { useAuthStore, useThemeStore } from '../../store';
import View from '../../components/view/view';
import { hasPermissions } from '../../utils/permissions';
import { RolePermission } from '../../types/user.type';

import styles from './promotions.module.scss';

const PromotionsView = () => {
    const navigate = useNavigate();

    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();
    const [params, setParams] = useSearchParams({ page: '1', count: '10' });

    const [totalPages, setTotalPages] = useState(1);
    const [tableRows, setTableRows] = useState<TableRow[]>([]);

    useEffect(() => {
        fetchCoupons(mapParams(params), permissions);
    }, [params, permissions]);

    const mapParams = useCallback((params: URLSearchParams) => {
        const filters: Record<string, string> = {};
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }

        return filters;
    }, []);

    const fetchCoupons = useCallback(
        async (filters: any, permissions: RolePermission[]) => {
            if (!hasPermissions('read-coupons', permissions)) return;

            const response = await couponSdk.getMany(filters);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                console.error('Error getting invoices:', response);
                return;
            }

            const { total } = response.data;
            setTotalPages(Math.ceil(total / Number(params.get('count'))));
            mapRows(response.data.result);
        },
        [params]
    );

    const mapRows = (coupons: Coupon[]) => {
        const couponRows = coupons.map((coupon) => {
            return {
                id: coupon.id,
                data: [
                    { type: 'number', value: coupon.id },
                    {
                        type: 'string',
                        value: coupon.code,
                    },
                    {
                        type: 'string',
                        value: `-${coupon.discount}%`,
                    },
                    {
                        type: 'string',
                        value: `${coupon.quantity} ${
                            coupon.quantity === 1 ? 'disponible' : 'disponibles'
                        }`,
                    },
                    { type: 'string', value: coupon.description || '-' },
                    {
                        type: 'string',
                        value: coupon.plan.description,
                    },
                    {
                        type: 'date',
                        value: new Date(coupon.issueDate),
                    },
                ],
            };
        });
        setTableRows(couponRows);
    };

    const goToView = (ev: MouseEvent, url: string) => {
        ev.preventDefault();
        navigate(url);
    };

    const goToPage = useCallback(
        (page: number) => {
            if (page < 1 || page > totalPages) return;

            params.set('page', page.toString());
            setParams(params);
        },
        [params, totalPages]
    );

    const addFilter = useCallback(
        (attr: string, value: string) => {
            if (!value || value.trim().length === 0) {
                params.delete(attr);
            } else {
                params.set(attr, value);
            }
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    const goToCouponDetails = (id: number) => {
        navigate(`/promotions/${id}`);
    };

    return (
        <View permissions={permissions} requiredPermission="read-coupons">
            <Header
                mode={theme}
                title={'Promociones'}
                subtitle={
                    'Aquí puedes ver cupones, descuentos y demás promociones'
                }
            />
            <ModuleSummary
                mode={theme}
                summary={[
                    { label: 'Total descuentos realizadas', value: '-' },
                    { label: 'Monto total descontado', value: '-' },
                    { label: 'Total cupones canjeados este mes', value: '-' },
                    { label: 'Fecha último descuento', value: '-' },
                ]}
            />
            <section className={styles.creationButtons}>
                <button
                    onClick={(ev) => goToView(ev, '/promotions/coupons/create')}
                >
                    Crear cupón
                </button>
                <button
                    onClick={(ev) =>
                        goToView(ev, '/promotions/gift-card/create')
                    }
                >
                    Crear gift card
                </button>
            </section>
            <div className={'view-filtering-section'}>
                <span className={'filtering-label'}>Buscar por:</span>
                <FilteringButton
                    defaultValue={params.get('code')}
                    text={'Código de cupón'}
                    onSetFilter={(code) =>
                        addFilter('code', code.toUpperCase())
                    }
                />
            </div>
            <Table
                mode={theme}
                header={[
                    'N° Coupón',
                    'Código',
                    'Descuento',
                    'Cantidad restante',
                    'Descripción',
                    'Plan',
                    'Fecha de emisión',
                ]}
                data={tableRows}
                currentPage={Number(params.get('page'))}
                totalPages={totalPages}
                onItemSelected={goToCouponDetails}
                onPageChange={goToPage}
            />
        </View>
    );
};

export default PromotionsView;
